import React, { useState } from "react";
import {
  Dialog,
  Typography,
  Alert,
  IconButton,
  TextField,
} from "@mui/material";
import { Close, ShoppingCart, Groups, CheckCircle } from "@mui/icons-material";
import Box from "themeComponents/Box";
import Button from "themeComponents/Button";

interface CoursePurchaseDialogProps {
  open: boolean;
  onClose: () => void;
  courseTitle: string;
  quotaPricing: Record<string, number>;
  baseCoursePrice: number;
  onProceed: (quota: number, price: number) => void;
  loading?: boolean;
}

function CoursePurchaseDialog({
  open,
  onClose,
  courseTitle,
  quotaPricing,
  baseCoursePrice,
  onProceed,
  loading = false,
}: CoursePurchaseDialogProps): React.JSX.Element {
  const quotaOptions = Object.entries(quotaPricing)
    .filter(([, price]) => price > 0)
    .sort(([a], [b]) => Number(a) - Number(b));

  const [selectedQuota, setSelectedQuota] = useState<string>(
    quotaOptions.length > 0 ? quotaOptions[0][0] : ""
  );
  const [customSeats, setCustomSeats] = useState<string>("");

  const selectedPrice = selectedQuota ? quotaPricing[selectedQuota] ?? 0 : 0;
  const parsedCustomSeats = Number(customSeats);
  const hasCustomSeats = Number.isInteger(parsedCustomSeats) && parsedCustomSeats > 0;
  const matchingTierPrice = hasCustomSeats
    ? quotaPricing[String(parsedCustomSeats)]
    : undefined;
  const customTotalPrice = hasCustomSeats
    ? matchingTierPrice ?? baseCoursePrice * parsedCustomSeats
    : 0;
  const usingCustomSeats = hasCustomSeats;
  const finalQuota = usingCustomSeats ? parsedCustomSeats : Number(selectedQuota || 0);
  const finalPrice = usingCustomSeats ? customTotalPrice : selectedPrice;

  const handleProceed = () => {
    if (!finalQuota || finalPrice <= 0) return;
    onProceed(finalQuota, finalPrice);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Premium header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #1976d2 100%)",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            bgcolor: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <ShoppingCart sx={{ color: "#fff", fontSize: 22 }} />
        </Box>

        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
          Purchase Course
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.75)",
            mt: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {courseTitle}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {quotaOptions.length === 0 ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            No pricing options are available for this course.
          </Alert>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Groups sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                Select number of seats
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap={1.25}>
              {quotaOptions.map(([quota, price]) => {
                const isSelected = selectedQuota === quota;
                const perSeat = Number(quota) > 1 ? (price / Number(quota)).toFixed(2) : null;
                return (
                  <Box
                    key={quota}
                    onClick={() => setSelectedQuota(quota)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      border: "2px solid",
                      borderColor: isSelected ? "#1976d2" : "rgba(0,0,0,0.08)",
                      bgcolor: isSelected ? "rgba(25, 118, 210, 0.06)" : "#fafafa",
                      cursor: "pointer",
                      transition: "all 0.18s ease",
                      boxShadow: isSelected ? "0 0 0 3px rgba(25,118,210,0.12)" : "none",
                      "&:hover": {
                        borderColor: isSelected ? "#1976d2" : "rgba(25,118,210,0.35)",
                        bgcolor: isSelected ? "rgba(25, 118, 210, 0.06)" : "rgba(25,118,210,0.02)",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: isSelected ? "#1976d2" : "rgba(0,0,0,0.06)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background 0.18s",
                        }}
                      >
                        {isSelected ? (
                          <CheckCircle sx={{ fontSize: 18, color: "#fff" }} />
                        ) : (
                          <Groups sx={{ fontSize: 16, color: "text.secondary" }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>
                          {quota} {Number(quota) === 1 ? "seat" : "seats"}
                        </Typography>
                        {perSeat && (
                          <Typography variant="caption" color="text.secondary">
                            ${perSeat} / seat
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box textAlign="right">
                      <Typography variant="body1" fontWeight={700} color={isSelected ? "#1976d2" : "text.primary"}>
                        ${price.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box mt={2.5}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                Or enter custom seats
              </Typography>
              <TextField
                fullWidth
                type="number"
                size="small"
                value={customSeats}
                onChange={(e) => setCustomSeats(e.target.value)}
                placeholder="Enter seats (e.g. 13)"
                inputProps={{ min: 1, step: 1 }}
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                {hasCustomSeats && matchingTierPrice !== undefined
                  ? `Matched package pricing for ${parsedCustomSeats} seats`
                  : `Custom total = $${Number(baseCoursePrice || 0).toFixed(2)} × seats`}
              </Typography>
            </Box>

            {/* Total summary */}
            {(selectedQuota || usingCustomSeats) && (
              <Box
                sx={{
                  mt: 2.5,
                  px: 2,
                  py: 1.75,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #f5f7ff 0%, #eef2fb 100%)",
                  border: "1px solid rgba(25,118,210,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                    Total due
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="#1a237e" sx={{ lineHeight: 1.1 }}>
                    ${finalPrice.toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {finalQuota} {finalQuota === 1 ? "seat" : "seats"}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, pt: 2, display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{ borderRadius: "10px", px: 2.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="gradient"
          color="info"
          size="small"
          onClick={handleProceed}
          disabled={!finalQuota || finalPrice <= 0 || loading}
          loading={loading}
          sx={{ borderRadius: "10px", px: 2.5 }}
        >
          <ShoppingCart sx={{ mr: 1, fontSize: 16 }} /> Proceed to Payment
        </Button>
      </Box>
    </Dialog>
  );
}

export default CoursePurchaseDialog;
